import { useMemo, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { useTenantContext } from '@/lib/hooks/use-tenant-context';
import { Plus, Play, Clock3, Send, Layers3, Cable, CircleAlert } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { EmptyState } from '@/components/shared/empty-state';
import { ROUTES } from '@/app/routes';
import { useDocumentTitle } from '@/lib/hooks/use-document-title';
import { useGetApiAgents } from '@/lib/api/generated/agents/agents';
import { useGetApiLightweightAutomations, usePostApiLightweightAutomationRunNow } from '@/lib/api/generated/automations/automations';
import { queryKeys } from '@/lib/query/keys';
import { useAutomationsUiStore } from '@/stores/automations-ui-store';
import { type AutomationRecord } from './automation-domain';
import { type AutomationTemplateKey } from './templates';

export function AutomationsPage() {
  const { tenantId: activeTenantId } = useTenantContext();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const tenantScopedQueryOptions = { query: { enabled: Boolean(activeTenantId) } };
  const { data: automations } = useGetApiLightweightAutomations(tenantScopedQueryOptions);
  const { data: agents } = useGetApiAgents(tenantScopedQueryOptions);
  const runMutation = usePostApiLightweightAutomationRunNow({
    mutation: {
      onSuccess: async (_result, variables) => {
        await queryClient.invalidateQueries({ queryKey: ['/api/lightweight-automations', { tenantId: activeTenantId }] });
        await queryClient.invalidateQueries({ queryKey: queryKeys.state });
        await queryClient.invalidateQueries({
          queryKey: queryKeys.automationRuns(activeTenantId, variables.data.automationId),
        });
      },
    },
  });
  const {
    search, setSearch,
    stateFilter, setStateFilter,
    deliveryFilter, setDeliveryFilter,
    scheduleFilter, setScheduleFilter,
    sortBy, setSortBy,
    resetFilters,
  } = useAutomationsUiStore();
  useDocumentTitle('Automations');

  const deliveryOptions = useMemo(
    () => Array.from(new Set((automations ?? []).map((entry) => entry.delivery.mode))).toSorted(),
    [automations],
  );
  const filteredAutomations = useMemo(
    () =>
      [...(automations ?? [])]
        .filter((entry) => matchesAutomationSearch(entry, search))
        .filter((entry) => deliveryFilter === 'all' || entry.delivery.mode === deliveryFilter)
        .filter((entry) =>
          scheduleFilter === 'all' ? true : entry.execution.mode === scheduleFilter,
        )
        .filter((entry) => {
          switch (stateFilter) {
            case 'enabled':
              return entry.enabled;
            case 'disabled':
              return !entry.enabled;
            case 'failed':
              return entry.lastRunStatus === 'failed';
            default:
              return true;
          }
        })
        .sort((left, right) => compareAutomations(left, right, sortBy)),
    [automations, deliveryFilter, scheduleFilter, search, sortBy, stateFilter],
  );
  const stats = useMemo(() => buildAutomationStats(automations ?? []), [automations]);
  const hasAgents = (agents?.length ?? 0) > 0;

  return (
    <div className="flex flex-col gap-6">
      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-4">
          <div className="flex flex-col gap-3">
            <div>
              <CardTitle>Automations</CardTitle>
              <CardDescription>
                Prompt-first automations with typed delivery, runtime-owned orchestration, and bounded execution.
              </CardDescription>
            </div>
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
              <MetricTile icon={<Layers3 size={14} />} label="Total" value={String(stats.total)} />
              <MetricTile icon={<Cable size={14} />} label="Enabled" value={String(stats.enabled)} />
              <MetricTile icon={<Clock3 size={14} />} label="Scheduled" value={String(stats.scheduled)} />
              <MetricTile icon={<CircleAlert size={14} />} label="Needs Attention" value={String(stats.attention)} />
            </div>
          </div>
          <Button size="sm" onClick={() => navigate(ROUTES.tenantAutomationNew(activeTenantId))}>
            <Plus size={14} />
            New automation
          </Button>
        </CardHeader>
      </Card>

      <Card>
        <CardHeader className="gap-3">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <CardTitle className="text-base">Filter and Sort</CardTitle>
              <CardDescription>
                {filteredAutomations.length} of {automations?.length ?? 0} automations shown.
              </CardDescription>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={resetFilters}
            >
              Reset
            </Button>
          </div>
        </CardHeader>
        <CardContent className="grid gap-3 md:grid-cols-2 xl:grid-cols-5">
          <ControlField label="Search">
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Name, ID, task..."
              className="form-input"
            />
          </ControlField>
          <ControlField label="State">
            <select
              value={stateFilter}
              onChange={(event) => setStateFilter(event.target.value as typeof stateFilter)}
              className="form-input"
            >
              <option value="all">All states</option>
              <option value="enabled">Enabled</option>
              <option value="disabled">Disabled</option>
              <option value="failed">Failed runs</option>
            </select>
          </ControlField>
          <ControlField label="Delivery">
            <select value={deliveryFilter} onChange={(event) => setDeliveryFilter(event.target.value)} className="form-input">
              <option value="all">All delivery modes</option>
              {deliveryOptions.map((mode) => (
                <option key={mode} value={mode}>
                  {mode}
                </option>
              ))}
            </select>
          </ControlField>
          <ControlField label="Schedule">
            <select
              value={scheduleFilter}
              onChange={(event) => setScheduleFilter(event.target.value as typeof scheduleFilter)}
              className="form-input"
            >
              <option value="all">Manual + scheduled</option>
              <option value="manual">Manual</option>
              <option value="scheduled">Scheduled</option>
            </select>
          </ControlField>
          <ControlField label="Sort">
            <select
              value={sortBy}
              onChange={(event) => setSortBy(event.target.value as typeof sortBy)}
              className="form-input"
            >
              <option value="attention">Needs attention first</option>
              <option value="name">Name</option>
              <option value="updated">Recently updated</option>
              <option value="last_run">Latest run</option>
            </select>
          </ControlField>
        </CardContent>
      </Card>

      {!filteredAutomations.length ? (
        automations?.length ? (
          <EmptyState
            title="No automations match these filters"
            description="Adjust the active filters or search terms to widen the result set."
            action={{
              label: 'Reset filters',
              onClick: resetFilters,
            }}
          />
        ) : (
          <AutomationZeroState tenantId={activeTenantId} hasAgents={hasAgents} onNavigate={navigate} />
        )
      ) : (
        <div className="grid gap-4 2xl:grid-cols-2">
          {filteredAutomations.map((automation) => {
            const isRunningThisCard =
              runMutation.isPending &&
              runMutation.variables?.automationId === automation.automationId;

            return (
              <Card
                key={automation.automationId}
                className="h-full overflow-hidden"
                style={{
                  borderColor: automation.lastRunStatus === 'failed' ? 'color-mix(in srgb, var(--danger) 32%, var(--border) 68%)' : undefined,
                }}
              >
                <CardHeader className="gap-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex min-w-0 flex-col gap-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <CardTitle className="text-lg">{automation.name}</CardTitle>
                        <Badge variant={automation.enabled ? 'active' : 'draft'}>
                          {automation.enabled ? 'enabled' : 'disabled'}
                        </Badge>
                        <Badge variant={automation.lastRunStatus === 'failed' ? 'pending' : 'outline'}>
                          {formatLastRunStatus(automation.lastRunStatus)}
                        </Badge>
                      </div>
                      <p className="text-xs font-mono" style={{ color: 'var(--muted-foreground)' }}>
                        {automation.automationId}
                      </p>
                    </div>
                  </div>

                  <div
                    className="rounded-sm border p-4"
                    style={{
                      background: 'var(--bg-elevated)',
                      borderColor: 'var(--border)',
                    }}
                  >
                    <div className="mb-2 text-[10px] font-semibold uppercase tracking-[0.14em]" style={{ color: 'var(--muted-foreground)' }}>
                      Task Preview
                    </div>
                    <p
                      className="text-sm leading-6"
                      style={{
                        color: 'var(--text-strong)',
                        display: '-webkit-box',
                        WebkitLineClamp: 4,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {summarizeInstructions(automation.instructionsMarkdown)}
                    </p>
                  </div>
                </CardHeader>

                <CardContent className="flex flex-col gap-4">
                  <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                    <MetaTile
                      icon={<Layers3 size={14} />}
                      label="Input"
                      value={formatInputSource(automation)}
                    />
                    <MetaTile
                      icon={<Cable size={14} />}
                      label="Delivery"
                      value={formatDelivery(automation)}
                    />
                    <MetaTile
                      icon={<Clock3 size={14} />}
                      label="Schedule"
                      value={formatSchedule(automation)}
                    />
                    <MetaTile
                      icon={<Send size={14} />}
                      label="Last Summary"
                      value={automation.lastRunSummary?.trim() || 'No completed run yet'}
                    />
                  </div>

                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Pill label={`Execution ${automation.execution.executionClass}`} />
                      <Pill label={`Mode ${automation.execution.mode}`} />
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => navigate(ROUTES.tenantAutomation(activeTenantId, automation.automationId))}
                      >
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        onClick={() =>
                          void runMutation.mutateAsync({
                            data: {
                              tenantId: activeTenantId,
                              automationId: automation.automationId,
                            },
                          })
                        }
                        disabled={isRunningThisCard}
                      >
                        <Play size={14} />
                        {isRunningThisCard ? 'Running...' : 'Run now'}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

const AUTOMATION_STARTERS: ReadonlyArray<{
  title: string;
  template: AutomationTemplateKey;
  description: string;
  time: string;
}> = [
  {
    title: 'News scan',
    template: 'research_news_digest',
    description: 'Track recent industry changes and deliver a structured message.',
    time: '5 min',
  },
  {
    title: 'CRM lead enrichment',
    template: 'crm_lead_enrichment',
    description: 'Enrich new leads with structured output that can be reviewed downstream.',
    time: '7 min',
  },
  {
    title: 'CRM prepared updates',
    template: 'crm_prepare_updates',
    description: 'Generate governed CRM follow-up actions without doing direct writes.',
    time: '7 min',
  },
];

function AutomationZeroState({
  tenantId,
  hasAgents,
  onNavigate,
}: {
  tenantId: string;
  hasAgents: boolean;
  onNavigate: (to: string) => void;
}) {
  const primaryPath = hasAgents
    ? ROUTES.tenantAutomationNew(tenantId)
    : ROUTES.tenantAgentNew(tenantId);

  return (
    <div className="grid gap-4 xl:grid-cols-[minmax(0,1.05fr)_minmax(0,0.95fr)]">
      <Card>
        <CardHeader className="gap-3">
          <div>
            <CardTitle>No automations yet</CardTitle>
            <CardDescription>
              Start with one concrete workflow instead of a blank list.
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <div className="grid gap-3 sm:grid-cols-3">
            <MetricTile icon={<Layers3 size={14} />} label="Step 1" value={hasAgents ? 'Agent ready' : 'Create an agent'} />
            <MetricTile icon={<Cable size={14} />} label="Step 2" value="Choose a starter" />
            <MetricTile icon={<Clock3 size={14} />} label="Step 3" value="Run and refine" />
          </div>
          <div
            className="rounded-sm border p-4 text-sm leading-6"
            style={{
              borderColor: 'var(--border)',
              background: 'var(--bg-elevated)',
              color: 'var(--text-strong)',
            }}
          >
            {hasAgents
              ? 'Automations attach to an agent, define the task in instructions, then deliver the result through a structured output mode.'
              : 'Automations attach to a tenant agent. Create one agent first, then come back here and start from a prefilled template.'}
          </div>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" onClick={() => onNavigate(primaryPath)}>
              <Plus size={14} />
              {hasAgents ? 'Create automation' : 'Create first agent'}
            </Button>
            <Button size="sm" variant="outline" onClick={() => onNavigate(ROUTES.tenant(tenantId))}>
              View setup overview
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Starter templates</CardTitle>
          <CardDescription>
            Each option opens the editor with a real template instead of an empty form.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-3">
          {AUTOMATION_STARTERS.map((starter) => {
            const path = hasAgents
              ? ROUTES.tenantAutomationNew(tenantId, starter.template)
              : ROUTES.tenantAgentNew(tenantId);

            return (
              <div
                key={starter.template}
                className="rounded-sm border p-4"
                style={{ borderColor: 'var(--border)', background: 'var(--panel)' }}
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-sm font-semibold" style={{ color: 'var(--text-strong)' }}>
                      {starter.title}
                    </div>
                    <p className="mt-1 text-xs leading-5" style={{ color: 'var(--muted-foreground)' }}>
                      {starter.description}
                    </p>
                  </div>
                  <Badge variant="outline">{starter.time}</Badge>
                </div>
                <div className="mt-4 flex items-center justify-between gap-3">
                  <span className="text-[11px] uppercase tracking-[0.14em]" style={{ color: 'var(--muted-foreground)' }}>
                    {hasAgents ? 'Prefilled automation' : 'Requires an agent first'}
                  </span>
                  <Button size="sm" variant="outline" onClick={() => onNavigate(path)}>
                    {hasAgents ? 'Use template' : 'Create agent first'}
                  </Button>
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>
    </div>
  );
}

function MetricTile({
  icon,
  label,
  value,
}: {
  icon: ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div
      className="rounded-sm border p-4"
      style={{
        borderColor: 'var(--border)',
        background: 'var(--bg-elevated)',
      }}
    >
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.14em]" style={{ color: 'var(--muted-foreground)' }}>
        {icon}
        {label}
      </div>
      <div className="mt-2 text-2xl font-semibold" style={{ color: 'var(--text-strong)' }}>
        {value}
      </div>
    </div>
  );
}

function MetaTile({
  icon,
  label,
  value,
}: {
  icon: ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div
      className="rounded-sm border p-3"
      style={{ background: 'var(--bg-elevated)', borderColor: 'var(--border)' }}
    >
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.14em]" style={{ color: 'var(--muted-foreground)' }}>
        {icon}
        {label}
      </div>
      <div
        className="mt-2 text-sm leading-5"
        style={{
          color: 'var(--text-strong)',
          display: '-webkit-box',
          WebkitLineClamp: 3,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}
      >
        {value}
      </div>
    </div>
  );
}

function Pill({ label }: { label: string }) {
  return (
    <span
      className="rounded-full border px-3 py-1 text-[11px]"
      style={{
        borderColor: 'var(--border)',
        background: 'var(--bg-elevated)',
        color: 'var(--muted-foreground)',
      }}
    >
      {label}
    </span>
  );
}

function buildAutomationStats(automations: AutomationRecord[]) {
  return {
    total: automations.length,
    enabled: automations.filter((entry) => entry.enabled).length,
    scheduled: automations.filter((entry) => entry.execution.mode === 'scheduled').length,
    attention: automations.filter((entry) => entry.lastRunStatus === 'failed' || !entry.enabled).length,
  };
}

function compareAutomations(
  left: AutomationRecord,
  right: AutomationRecord,
  sortBy: 'attention' | 'name' | 'updated' | 'last_run',
) {
  if (sortBy === 'name') {
    return left.name.localeCompare(right.name);
  }
  if (sortBy === 'updated') {
    return compareTimestamps(right.updatedAt, left.updatedAt);
  }
  if (sortBy === 'last_run') {
    return compareTimestamps(right.lastRunAt, left.lastRunAt);
  }
  const leftAttention = left.lastRunStatus === 'failed' ? 0 : left.enabled ? 1 : 2;
  const rightAttention = right.lastRunStatus === 'failed' ? 0 : right.enabled ? 1 : 2;
  if (leftAttention !== rightAttention) {
    return leftAttention - rightAttention;
  }
  return left.name.localeCompare(right.name);
}

function compareTimestamps(left?: string, right?: string) {
  const leftTime = left ? new Date(left).getTime() : 0;
  const rightTime = right ? new Date(right).getTime() : 0;
  return leftTime - rightTime;
}

function matchesAutomationSearch(automation: AutomationRecord, search: string) {
  const needle = search.trim().toLowerCase();
  if (!needle) {
    return true;
  }
  const haystack = [
    automation.name,
    automation.automationId,
    automation.instructionsMarkdown,
    automation.lastRunSummary ?? '',
  ]
    .join(' ')
    .toLowerCase();
  return haystack.includes(needle);
}

function ControlField({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-[10px] font-semibold uppercase tracking-[0.14em]" style={{ color: 'var(--muted-foreground)' }}>
        {label}
      </span>
      {children}
    </label>
  );
}

function summarizeInstructions(instructionsMarkdown: string) {
  const lines = instructionsMarkdown
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
  if (lines.length === 0) {
    return 'No instructions yet.';
  }
  return lines.join(' ');
}

function formatInputSource(automation: AutomationRecord) {
  switch (automation.inputSource.kind) {
    case 'none':
      return 'No external input';
    case 'static_list':
      return `${automation.inputSource.items.length} static item${automation.inputSource.items.length === 1 ? '' : 's'}`;
    case 'http':
      return `${automation.inputSource.method ?? 'GET'} ${automation.inputSource.url}`;
    case 'webhook':
      return 'Inbound webhook payload';
    case 'connector':
      return automation.inputSource.toolName;
    default:
      return automation.inputSource.kind;
  }
}

function formatDelivery(automation: AutomationRecord) {
  switch (automation.delivery.mode) {
    case 'api_batch':
      return automation.delivery.apiBatch?.url ?? 'API batch';
    case 'webhook':
      return automation.delivery.webhook?.url ?? 'Webhook';
    case 'message':
      return automation.delivery.message?.destination ?? 'Message';
    default:
      return 'No delivery';
  }
}

function formatLastRunStatus(status?: string) {
  if (!status) {
    return 'never run';
  }
  return status.replace(/_/g, ' ');
}

function formatSchedule(automation: AutomationRecord) {
  if (automation.execution.mode !== 'scheduled') {
    return 'Manual only';
  }
  const schedule = automation.execution.schedule;
  if (!schedule) {
    return 'Scheduled';
  }
  if (schedule.kind === 'cron') {
    return `${schedule.cron ?? 'unspecified'}${schedule.tz ? ` • ${schedule.tz}` : ''}`;
  }
  if (schedule.kind === 'every') {
    return schedule.everyMs ? `Every ${schedule.everyMs}ms` : 'Recurring';
  }
  return schedule.at ? `At ${schedule.at}` : 'Scheduled';
}
